import { Menu } from '@headlessui/react';
import {
  ArrowsExpandIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  SelectorIcon,
  XIcon,
} from '@heroicons/react/outline';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FaFileCsv, FaFileExcel, FaRegCopy } from 'react-icons/fa';
import { toast } from 'sonner';

import { copyRows, saveBlob, toCsv } from '@/lib/spreadsheet/tableText';
import { numericValue } from '@/lib/spreadsheet/values';

import {
  DOCUMENT_MENU_PANEL_CLASS,
  DOCUMENT_MENU_WIDTH,
  MenuButtonItem,
  MenuPortal,
} from './MessageParts/documentMenu';

/*
 * A table in a Markdown reply, with the actions people reach for: copy it
 * (pastes into Excel and Google Sheets as cells), download it as CSV or
 * Excel, and open it full screen with sortable columns.
 */

/** The text of each cell, row by row (what Copy and Download use). */
function readRows(table: HTMLTableElement | null): string[][] {
  if (!table) return [];
  return Array.from(table.rows).map((row) =>
    Array.from(row.cells).map((cell) =>
      (cell.innerText ?? cell.textContent ?? '').replace(/\s+\n/g, '\n').trim(),
    ),
  );
}

/** A copy of a cell's content, without anything that could run code. */
function safeCellHtml(cell: HTMLTableCellElement): string {
  const copy = cell.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('*').forEach((node) => {
    for (const attribute of Array.from(node.attributes)) {
      const name = attribute.name.toLowerCase();
      if (name.startsWith('on')) node.removeAttribute(attribute.name);
      else if (
        (name === 'href' || name === 'src' || name === 'xlink:href') &&
        /^\s*(javascript|data|vbscript):/i.test(attribute.value) &&
        !(name === 'src' && /^\s*data:image\//i.test(attribute.value))
      ) {
        node.removeAttribute(attribute.name);
      }
    }
    if (node.tagName === 'SCRIPT' || node.tagName === 'IFRAME') node.remove();
  });
  return copy.innerHTML;
}

const TOOL_BUTTON =
  'flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-200';

function useCopy(getRows: () => string[][]) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const copy = useCallback(() => {
    copyRows(getRows())
      .then(() => {
        setCopied(true);
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 1800);
      })
      .catch(() => toast.error('Couldn’t copy the table.'));
  }, [getRows]);
  return { copied, copy };
}

async function downloadRows(rows: string[][], format: 'csv' | 'xlsx') {
  if (rows.length === 0) return;
  try {
    if (format === 'csv') {
      saveBlob(
        new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }),
        'table.csv',
      );
      return;
    }
    const { writeXlsx } = await import('@/lib/spreadsheet/xlsxWriter');
    const blob = (await writeXlsx([{ name: 'Table', rows }])) as Blob;
    saveBlob(blob, 'table.xlsx');
  } catch {
    toast.error('Couldn’t create the file. Please try again.');
  }
}

function DownloadMenu({ getRows }: { getRows: () => string[][] }) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  return (
    <Menu as='div' className='relative'>
      {({ open, close }) => (
        <>
          <Menu.Button
            ref={buttonRef}
            aria-label='Download table'
            title='Download'
            className={TOOL_BUTTON}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Escape' && open) {
                e.preventDefault();
                close();
              }
            }}
          >
            <DownloadIcon className='h-4 w-4' />
          </Menu.Button>
          {open && (
            <MenuPortal anchorRef={buttonRef}>
              <Menu.Items
                static
                className={DOCUMENT_MENU_PANEL_CLASS}
                style={{ width: DOCUMENT_MENU_WIDTH }}
              >
                <MenuButtonItem
                  icon={<FaFileExcel className='h-4 w-4 text-[#107C41]' />}
                  label='Excel (.xlsx)'
                  hint='Numbers stay numbers'
                  onSelect={() => void downloadRows(getRows(), 'xlsx')}
                />
                <MenuButtonItem
                  icon={
                    <FaFileCsv className='h-4 w-4 text-gray-600 dark:text-gray-300' />
                  }
                  label='CSV'
                  hint='Opens in any spreadsheet app'
                  onSelect={() => void downloadRows(getRows(), 'csv')}
                />
              </Menu.Items>
            </MenuPortal>
          )}
        </>
      )}
    </Menu>
  );
}

/* ------------------------------------------------------------------ */
/* Full-screen view                                                    */
/* ------------------------------------------------------------------ */

interface ExpandedTable {
  header: { html: string; text: string }[];
  body: { html: string; text: string }[][];
}

function readExpanded(table: HTMLTableElement): ExpandedTable {
  const rows = Array.from(table.rows);
  const toCells = (row: HTMLTableRowElement) =>
    Array.from(row.cells).map((cell) => ({
      html: safeCellHtml(cell),
      text: (cell.innerText ?? cell.textContent ?? '').trim(),
    }));
  const hasHeader = rows[0] && rows[0].parentElement?.tagName === 'THEAD';
  return {
    header: hasHeader ? toCells(rows[0]) : [],
    body: rows.slice(hasHeader ? 1 : 0).map(toCells),
  };
}

function compareText(a: string, b: string) {
  const na = numericValue(a);
  const nb = numericValue(b);
  if (na !== null && nb !== null) return na - nb;
  if (na !== null) return -1;
  if (nb !== null) return 1;
  if (!a && b) return 1;
  if (a && !b) return -1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function ExpandedTableView({
  table,
  getRows,
  onClose,
}: {
  table: ExpandedTable;
  getRows: () => string[][];
  onClose: () => void;
}) {
  const [sort, setSort] = useState<{ col: number; dir: 1 | -1 } | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previousFocus?.focus?.();
    };
  }, [onClose]);

  const rows = useMemo(() => {
    if (!sort) return table.body;
    return [...table.body].sort(
      (a, b) =>
        compareText(a[sort.col]?.text ?? '', b[sort.col]?.text ?? '') *
        sort.dir,
    );
  }, [sort, table.body]);

  // Copy and Download take the rows in the order shown.
  const shownRows = useCallback(
    () =>
      sort
        ? [
            ...(table.header.length ? [table.header.map((c) => c.text)] : []),
            ...rows.map((row) => row.map((c) => c.text)),
          ]
        : getRows(),
    [getRows, rows, sort, table.header],
  );
  const { copied, copy } = useCopy(shownRows);

  const toggleSort = (col: number) =>
    setSort((current) =>
      !current || current.col !== col
        ? { col, dir: 1 }
        : current.dir === 1
          ? { col, dir: -1 }
          : null,
    );

  return createPortal(
    <div
      className='fixed inset-0 z-[110] flex items-stretch justify-center bg-gray-900/50 backdrop-blur-[2px] sm:p-6'
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        role='dialog'
        aria-modal='true'
        aria-label='Table'
        className='flex h-full w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-gray-900 sm:rounded-2xl'
      >
        <div className='flex items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800'>
          <div className='min-w-0 flex-1'>
            <div className='text-sm font-semibold text-gray-900 dark:text-gray-100'>
              Table
            </div>
            <div className='text-xs text-gray-500 dark:text-gray-400'>
              {table.body.length.toLocaleString()}{' '}
              {table.body.length === 1 ? 'row' : 'rows'}
              {table.header.length > 0 && ' · Click a column to sort'}
            </div>
          </div>
          <button
            type='button'
            onClick={copy}
            className='flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
          >
            {copied ? (
              <CheckIcon className='h-4 w-4 text-green-600' />
            ) : (
              <FaRegCopy className='h-3.5 w-3.5' />
            )}
            {copied ? 'Copied' : 'Copy'}
          </button>
          <div className='flex h-8 items-center rounded-lg border border-gray-200 bg-white px-1 shadow-sm dark:border-gray-600 dark:bg-gray-800'>
            <DownloadMenu getRows={shownRows} />
          </div>
          <button
            ref={closeRef}
            type='button'
            onClick={onClose}
            aria-label='Close'
            className='flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
          >
            <XIcon className='h-5 w-5' />
          </button>
        </div>
        <div className='messages min-h-0 flex-1 overflow-auto'>
          <table className='chat-table-expanded'>
            {table.header.length > 0 && (
              <thead>
                <tr>
                  {table.header.map((cell, i) => (
                    <th
                      key={i}
                      aria-sort={
                        sort?.col === i
                          ? sort.dir === 1
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                    >
                      <button
                        type='button'
                        onClick={() => toggleSort(i)}
                        className='flex w-full items-center gap-1 text-left'
                        title='Sort'
                      >
                        <span dangerouslySetInnerHTML={{ __html: cell.html }} />
                        <span className='ml-auto flex-shrink-0 pl-1 text-gray-400'>
                          {sort?.col === i ? (
                            sort.dir === 1 ? (
                              <ChevronUpIcon className='h-3.5 w-3.5 text-primary-600' />
                            ) : (
                              <ChevronDownIcon className='h-3.5 w-3.5 text-primary-600' />
                            )
                          ) : (
                            <SelectorIcon className='h-3.5 w-3.5' />
                          )}
                        </span>
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      dangerouslySetInnerHTML={{ __html: cell.html }}
                    />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/* Table                                                               */
/* ------------------------------------------------------------------ */

export default function ChatTable({ children }: { children: React.ReactNode }) {
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [expanded, setExpanded] = useState<ExpandedTable | null>(null);
  const getRows = useCallback(() => readRows(tableRef.current), []);
  const { copied, copy } = useCopy(getRows);
  const close = useCallback(() => setExpanded(null), []);

  return (
    <div className='md-table-block group/table relative'>
      <div className='md-table-toolbar absolute bottom-full right-0 mb-0.5 flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover/table:opacity-100 [@media(hover:none)]:opacity-100'>
        <button
          type='button'
          onClick={copy}
          aria-label='Copy table'
          title={copied ? 'Copied' : 'Copy (pastes into Excel and Sheets)'}
          className={TOOL_BUTTON}
        >
          {copied ? (
            <CheckIcon className='h-4 w-4 text-green-600' />
          ) : (
            <FaRegCopy className='h-3.5 w-3.5' />
          )}
        </button>
        <DownloadMenu getRows={getRows} />
        <button
          type='button'
          onClick={() =>
            tableRef.current && setExpanded(readExpanded(tableRef.current))
          }
          aria-label='Open full screen'
          title='Full screen'
          className={TOOL_BUTTON}
        >
          <ArrowsExpandIcon className='h-4 w-4' />
        </button>
      </div>
      <div className='md-table-wrapper'>
        <table ref={tableRef}>{children}</table>
      </div>
      {expanded && (
        <ExpandedTableView table={expanded} getRows={getRows} onClose={close} />
      )}
    </div>
  );
}
